import { useClerk, useUser } from '@clerk/react'
import { useEffect, useId, useRef, useState } from 'react'

type AccountControlProps = {
  compact?: boolean
}

function AccountIcon({ kind }: { kind: 'chevron' | 'profile' | 'signout' }) {
  const paths = {
    chevron: <path d="m8 10 4 4 4-4" />,
    profile: <><circle cx="12" cy="8" r="3" /><path d="M5 20a7 7 0 0 1 14 0M19 4v4M17 6h4" /></>,
    signout: <><path d="M10 17l5-5-5-5M15 12H3" /><path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" /></>,
  }

  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {paths[kind]}
    </svg>
  )
}

function AccountAvatar({ imageUrl, name }: { imageUrl?: string; name: string }) {
  const initial = name.trim().charAt(0).toUpperCase() || 'H'

  return imageUrl ? (
    <img src={imageUrl} alt="" className="size-8 shrink-0 rounded-full object-cover" aria-hidden />
  ) : (
    <span className="grid size-8 shrink-0 place-items-center rounded-full bg-accent text-[12px] font-semibold text-white" aria-hidden>
      {initial}
    </span>
  )
}

export function AccountControl({ compact = false }: AccountControlProps) {
  const clerk = useClerk()
  const { isLoaded, user } = useUser()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const popoverId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const firstActionRef = useRef<HTMLButtonElement>(null)

  const name = user?.fullName || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Hey Tasky account'
  const email = user?.primaryEmailAddress?.emailAddress || user?.emailAddresses?.[0]?.emailAddress || ''
  const secondary = email || user?.username || 'Signed in to Hey Tasky'
  const identityLabel = email ? `${name}, ${email}` : name

  useEffect(() => {
    if (!open) return

    const frame = window.requestAnimationFrame(() => firstActionRef.current?.focus())
    const handlePointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      setOpen(false)
      triggerRef.current?.focus()
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      window.cancelAnimationFrame(frame)
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  const openProfile = async () => {
    setError('')
    setOpen(false)
    try {
      await clerk.openUserProfile()
    } catch {
      setError('Could not open account settings. Try again.')
      setOpen(true)
    }
  }

  const signOut = async () => {
    setError('')
    setBusy(true)
    try {
      await clerk.signOut({ redirectUrl: '/sign-in' })
    } catch {
      setBusy(false)
      setError('Could not sign out. Try again.')
    }
  }

  if (!isLoaded) {
    return compact ? (
      <div className="grid size-11 place-items-center" role="status" aria-label="Loading Hey Tasky account">
        <span className="size-8 animate-pulse rounded-full bg-inset" />
      </div>
    ) : (
      <div className="flex h-12 w-full items-center gap-2 px-2" role="status" aria-label="Loading Hey Tasky account">
        <span className="size-8 animate-pulse rounded-full bg-inset" />
        <span className="min-w-0 flex-1 space-y-1.5">
          <span className="block h-2.5 w-24 animate-pulse rounded bg-inset" />
          <span className="block h-2 w-32 animate-pulse rounded bg-inset" />
        </span>
      </div>
    )
  }

  return (
    <div ref={rootRef} className={compact ? 'relative' : 'relative w-full'} data-testid="account-control">
      <button
        ref={triggerRef}
        type="button"
        className={compact
          ? 'grid size-11 place-items-center rounded-control transition hover:bg-hover active:scale-[0.96]'
          : 'group flex h-12 w-full items-center gap-2 rounded-control px-2 text-left transition hover:bg-hover active:scale-[0.98]'}
        aria-label={`${open ? 'Close' : 'Open'} Hey Tasky account menu for ${identityLabel}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={() => {
          setError('')
          setOpen((value) => !value)
        }}
      >
        <AccountAvatar imageUrl={user?.imageUrl} name={name} />
        {!compact && (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12.5px] font-medium leading-[1.25] text-ink-900">{name}</span>
              <span className="mt-0.5 block truncate text-[12px] leading-[1.25] text-ink-600">{secondary}</span>
            </span>
            <span className={`text-ink-400 transition-transform duration-150 ${open ? 'rotate-180 text-ink-600' : 'group-hover:text-ink-600'}`}>
              <AccountIcon kind="chevron" />
            </span>
          </>
        )}
      </button>

      {open && (
        <div
          id={popoverId}
          role="dialog"
          aria-label={`Hey Tasky account actions for ${identityLabel}`}
          className={compact
            ? 'fixed right-3 top-14 z-[70] w-[min(268px,calc(100vw-24px))] rounded-[12px] bg-surface p-1.5 shadow-overlay'
            : 'absolute bottom-[calc(100%+8px)] left-0 z-[70] w-full rounded-[12px] bg-surface p-1.5 shadow-overlay'}
        >
          <p className="px-2 pb-1.5 pt-1 text-[10px] font-medium uppercase tracking-[0.08em] text-ink-600">Hey Tasky account</p>
          <button
            ref={firstActionRef}
            type="button"
            onClick={openProfile}
            className="flex min-h-11 w-full items-center gap-2 rounded-control px-2 text-left text-ink-700 transition hover:bg-hover hover:text-ink-900 active:scale-[0.98]"
          >
            <span className="grid size-7 shrink-0 place-items-center rounded-[7px] bg-inset text-ink-600"><AccountIcon kind="profile" /></span>
            <span className="min-w-0 flex-1">
              <span className="block text-[12.5px] font-medium leading-tight">Manage account</span>
              <span className="mt-0.5 block text-[11px] leading-tight text-ink-600">Profile, email and security</span>
            </span>
          </button>
          <button
            type="button"
            onClick={signOut}
            disabled={busy}
            className="mt-0.5 flex min-h-11 w-full items-center gap-2 rounded-control px-2 text-left text-ink-700 transition hover:bg-hover hover:text-ink-900 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50"
          >
            <span className="grid size-7 shrink-0 place-items-center rounded-[7px] bg-inset text-ink-600"><AccountIcon kind="signout" /></span>
            <span className="text-[12.5px] font-medium">{busy ? 'Signing out...' : 'Sign out of Hey Tasky'}</span>
          </button>
          {error && <p className="mx-2 mb-1 mt-1.5 text-[11.5px] leading-snug text-red" role="alert">{error}</p>}
        </div>
      )}
    </div>
  )
}
